import {inject, Service, Signal} from '@angular/core';
import {User} from '../../models/user.model';
import {HttpClient} from '@angular/common/http';
import {filter, Observable} from 'rxjs';
import {map, share} from 'rxjs/operators';
import {Store} from '@ngxs/store';
import {SessionState} from 'src/app/shared/state/session.state';
import {BillingPermission, SUPERUSER_PERMISSION} from 'src/app/shared/auth/billing-permissions';

@Service()
export class UserService {
  private http = inject(HttpClient);
  private store = inject(Store);

  /**
   * The signed in user, or undefined until /api/user has loaded
   */
  readonly user: Signal<User | undefined> = this.store.selectSignal(SessionState.account);

  get(): Observable<User> {
    return this.store
      .select(SessionState.account)
      .pipe(
        filter(user => user != null),
      );
  }

  getSnapshot(): User | undefined {
    return this.store.selectSnapshot(SessionState.account);
  }

  /**
   * Fetch User entity from server
   */
  fetch(): Observable<User> {
    return this.http
      .get<User>('/api/user')
      .pipe(
        share()
      );
  }

  /**
   * Return true if the signed in user has all the named permissions.  Reads the user signal, so it is reactive when
   * called from a template, computed() or effect().
   */
  can(permission: BillingPermission | BillingPermission[]): boolean {
    return this.hasPermissionSnapshot(permission, this.user());
  }

  /**
   * Return true if user has the named permissions
   */
  hasPermission(permission: BillingPermission | BillingPermission[]): Observable<boolean> {
    return this.get()
      .pipe(
        map(u => this.hasPermissionSnapshot(permission, u))
      );
  }

  /**
   * Return true if specified User has the named permissions.  billing:all grants every permission.
   */
  hasPermissionSnapshot(permission: BillingPermission | BillingPermission[], user: User | undefined): boolean {
    if (!user) {
      return false;
    }

    if (user.permissions.includes(SUPERUSER_PERMISSION)) {
      return true;
    }

    const permissions = Array.isArray(permission) ? permission : [permission];

    return permissions.every((p) => user.permissions.includes(p));
  }
}
